// 问卷步骤1：发行主体资质核查
// 包含企业基本信息、评级和规模等内容

import React from 'react';
import {
  Form,
  Input,
  Select,
  Radio,
  DatePicker,
  InputNumber,
  Space,
  Typography,
  Row,
  Col,
  Card,
  Divider,
} from 'antd';
import {
  BankOutlined,
  IdcardOutlined,
  CrownOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  EnterpriseType,
  RegistrationType,
  CreditRating,
  InternationalRating,
  AssetScale,
  Industry,
  InitialScreeningQuestionnaire,
} from '../../../types/questionnaire';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface QuestionnaireStep1Props {
  form: any;
  questionnaire: Partial<InitialScreeningQuestionnaire>;
  onUpdate: (data: Partial<InitialScreeningQuestionnaire>) => void;
}

// 选项标签映射 - 使用翻译函数
const getEnterpriseTypeLabels = (t: (key: string) => string) => ({
  [EnterpriseType.CENTRAL_SOE]: t(
    'questionnaire:options.enterpriseType.central_soe'
  ),
  [EnterpriseType.LOCAL_SOE]: t(
    'questionnaire:options.enterpriseType.local_soe'
  ),
  local_soe_city_investment: t(
    'questionnaire:options.enterpriseType.local_soe_city_investment'
  ),
  [EnterpriseType.LISTED_COMPANY]: t(
    'questionnaire:options.enterpriseType.listed_company'
  ),
  [EnterpriseType.PRIVATE_ENTERPRISE]: t(
    'questionnaire:options.enterpriseType.private_enterprise'
  ),
  [EnterpriseType.FOREIGN_ENTERPRISE]: t(
    'questionnaire:options.enterpriseType.foreign_enterprise'
  ),
  [EnterpriseType.OTHER]: t('questionnaire:options.enterpriseType.other'),
});

const getRegistrationTypeLabels = (t: (key: string) => string) => ({
  [RegistrationType.MAINLAND_CHINA]: t(
    'questionnaire:options.registrationType.mainland_china'
  ),
  overseas: t('questionnaire:options.registrationType.overseas'),
});

const getCreditRatingLabels = (t: (key: string) => string) => ({
  [CreditRating.AAA]: t('questionnaire:options.creditRating.aaa'),
  [CreditRating.AA_PLUS]: t('questionnaire:options.creditRating.aa_plus'),
  [CreditRating.AA]: t('questionnaire:options.creditRating.aa'),
  [CreditRating.AA_MINUS]: t('questionnaire:options.creditRating.aa_minus'),
  [CreditRating.A_PLUS_AND_BELOW]: t(
    'questionnaire:options.creditRating.a_plus_and_below'
  ),
  [CreditRating.NO_RATING]: t('questionnaire:options.creditRating.no_rating'),
});

const getInternationalRatingLabels = (t: (key: string) => string) => ({
  [InternationalRating.INVESTMENT_GRADE]: t(
    'questionnaire:options.internationalRating.investment_grade'
  ),
  [InternationalRating.NON_INVESTMENT_GRADE]: t(
    'questionnaire:options.internationalRating.non_investment_grade'
  ),
  [InternationalRating.NO_INTERNATIONAL_RATING]: t(
    'questionnaire:options.internationalRating.no_international_rating'
  ),
});

const getAssetScaleLabels = (t: (key: string) => string) => ({
  [AssetScale.ABOVE_100B]: t('questionnaire:options.assetScale.above_100b'),
  [AssetScale.FROM_50B_TO_100B]: t(
    'questionnaire:options.assetScale.from_50b_to_100b'
  ),
  [AssetScale.FROM_20B_TO_50B]: t(
    'questionnaire:options.assetScale.from_20b_to_50b'
  ),
  [AssetScale.FROM_10B_TO_20B]: t(
    'questionnaire:options.assetScale.from_10b_to_20b'
  ),
  [AssetScale.FROM_5B_TO_10B]: t(
    'questionnaire:options.assetScale.from_5b_to_10b'
  ),
  [AssetScale.BELOW_5B]: t('questionnaire:options.assetScale.below_5b'),
});

const getIndustryLabels = (t: (key: string) => string) => ({
  [Industry.ENERGY]: t('questionnaire:options.industry.energy'),
  [Industry.INFRASTRUCTURE]: t('questionnaire:options.industry.infrastructure'),
  [Industry.FINANCIAL_SERVICES]: t(
    'questionnaire:options.industry.financial_services'
  ),
  [Industry.REAL_ESTATE]: t('questionnaire:options.industry.real_estate'),
  [Industry.MANUFACTURING]: t('questionnaire:options.industry.manufacturing'),
  [Industry.CONSUMER_SERVICES]: t(
    'questionnaire:options.industry.consumer_services'
  ),
  [Industry.TECHNOLOGY]: t('questionnaire:options.industry.technology'),
  [Industry.OTHER]: t('questionnaire:options.industry.other'),
});

export const QuestionnaireStep1: React.FC<QuestionnaireStep1Props> = ({
  form,
  questionnaire,
  onUpdate,
}) => {
  const { t } = useTranslation();

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      {/* A1. 发行人基本信息 */}
      <Card
        className="questionnaire-section-card"
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <BankOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:form.issuerBasicInfo')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerBasicInfo', 'issuerName']}
              label={t('questionnaire:step1.issuerName')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.issuerNameRequired'),
                },
              ]}
            >
              <Input
                placeholder={t('questionnaire:step1.issuerNamePlaceholder')}
                prefix={<BankOutlined style={{ color: '#10b981' }} />}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerBasicInfo', 'registrationNumber']}
              label={t('questionnaire:step1.registrationNumber')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.registrationNumberRequired'),
                },
                {
                  min: 8,
                  message: t('questionnaire:step1.registrationNumberMin'),
                },
                {
                  max: 30,
                  message: t('questionnaire:step1.registrationNumberMax'),
                },
              ]}
            >
              <Input
                placeholder={t(
                  'questionnaire:step1.registrationNumberPlaceholder'
                )}
                prefix={<IdcardOutlined style={{ color: '#10b981' }} />}
                maxLength={30}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerBasicInfo', 'enterpriseType']}
              label={t('questionnaire:step1.enterpriseType')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.enterpriseTypeRequired'),
                },
              ]}
            >
              <Select
                placeholder={t('questionnaire:step1.enterpriseTypePlaceholder')}
              >
                {Object.entries(getEnterpriseTypeLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerBasicInfo', 'registrationLocation']}
              label={t('questionnaire:step1.registrationLocation')}
              rules={[
                {
                  required: true,
                  message: t(
                    'questionnaire:step1.registrationLocationRequired'
                  ),
                },
              ]}
            >
              <Select
                placeholder={t(
                  'questionnaire:step1.registrationLocationPlaceholder'
                )}
              >
                {Object.entries(getRegistrationTypeLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              name={['issuerBasicInfo', 'hasGuarantor']}
              label={t('questionnaire:step1.hasGuarantor')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.hasGuarantorRequired'),
                },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>
                  {t('questionnaire:step1.withGuarantor')}
                </Radio>
                <Radio value={false}>
                  {t('questionnaire:step1.withoutGuarantor')}
                </Radio>
              </Radio.Group>
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.issuerBasicInfo?.hasGuarantor !==
                currentValues.issuerBasicInfo?.hasGuarantor
              }
            >
              {({ getFieldValue }) =>
                getFieldValue(['issuerBasicInfo', 'hasGuarantor']) && (
                  <Form.Item
                    name={['issuerBasicInfo', 'guarantorName']}
                    label={t('questionnaire:step1.guarantorName')}
                    rules={[
                      {
                        required: true,
                        message: t('questionnaire:step1.guarantorNameRequired'),
                      },
                    ]}
                  >
                    <Input
                      placeholder={t(
                        'questionnaire:step1.guarantorNamePlaceholder'
                      )}
                      prefix={<CrownOutlined style={{ color: '#10b981' }} />}
                    />
                  </Form.Item>
                )
              }
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* A2. 发行人评级与规模 */}
      <Card
        className="questionnaire-section-card"
        title={
          <div
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.05) 100%)',
              margin: '-12px -16px 12px -16px',
              padding: '12px 16px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid rgba(16, 185, 129, 0.1)',
            }}
          >
            <Space>
              <CrownOutlined style={{ color: '#10b981' }} />
              <Text strong>{t('questionnaire:form.issuerRatingScale')}</Text>
            </Space>
          </div>
        }
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerRatingScale', 'creditRating']}
              label={t('questionnaire:step1.creditRating')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.creditRatingRequired'),
                },
              ]}
              extra={t('questionnaire:step1.creditRatingExtra')}
            >
              <Select
                placeholder={t('questionnaire:step1.creditRatingPlaceholder')}
              >
                {Object.entries(getCreditRatingLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerRatingScale', 'internationalRating']}
              label={t('questionnaire:step1.internationalRating')}
              extra={t('questionnaire:step1.internationalRatingExtra')}
            >
              <Select
                placeholder={t(
                  'questionnaire:step1.internationalRatingPlaceholder'
                )}
                allowClear
              >
                {Object.entries(getInternationalRatingLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerRatingScale', 'totalAssets']}
              label={t('questionnaire:step1.totalAssets')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.totalAssetsRequired'),
                },
              ]}
              extra={t('questionnaire:step1.totalAssetsExtra')}
            >
              <Select
                placeholder={t('questionnaire:step1.totalAssetsPlaceholder')}
              >
                {Object.entries(getAssetScaleLabels(t)).map(
                  ([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              name={['issuerRatingScale', 'industry']}
              label={t('questionnaire:step1.industry')}
              rules={[
                {
                  required: true,
                  message: t('questionnaire:step1.industryRequired'),
                },
              ]}
              extra={t('questionnaire:step1.industryExtra')}
            >
              <Select
                placeholder={t('questionnaire:step1.industryPlaceholder')}
              >
                {Object.entries(getIndustryLabels(t)).map(([value, label]) => (
                  <Option key={value} value={value}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* 温馨提示 */}
      <Card
        className="questionnaire-tip-card"
        style={{
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(16, 185, 129, 0.1)',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.08)',
          transition: 'all 0.3s ease',
        }}
        styles={{ body: { padding: '16px' } }}
      >
        <Space wrap>
          <GlobalOutlined style={{ color: '#10b981' }} />
          <Text style={{ color: '#10b981' }}>
            <strong>{t('questionnaire:form.tip')}：</strong>
            {t('questionnaire:form.tipContent')}
          </Text>
        </Space>
      </Card>
    </Space>
  );
};
